import { createApi } from '@reduxjs/toolkit/query/react';

import { createBaseQueryWithReauth } from '@/services';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import { API_BUSINESS_URL } from '@/utils';

import { ZoneType, NewZoneType, UpdateZoneType } from '../models';

const zoneTypesService = createApi({
  reducerPath: 'zoneTypesService',
  baseQuery: createBaseQueryWithReauth(`${API_BUSINESS_URL}`),
  tagTypes: ['ZoneType', 'ZoneTypes'] as const,
  endpoints: (builder) => ({
    getZoneTypes: builder.query<
      PaginatedResponse<ZoneType>,
      {
        paginated: PaginatedRequest;
        search?: string;
      }
    >({
      query: ({ paginated, search }) => ({
        url: '/zone-types',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(search && search.trim() !== '' && { name: search.trim() }),
        },
      }),
      providesTags: ['ZoneTypes'],
    }),
    getZoneTypeById: builder.query<ZoneType, { zoneTypeId: string }>({
      query: ({ zoneTypeId }) => ({
        url: `/zone-type/${zoneTypeId}`,
      }),
      providesTags: ['ZoneType'],
    }),
    createZoneType: builder.mutation<ZoneType, NewZoneType>({
      query: (body) => ({
        url: '/zone-types',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ZoneTypes'],
    }),
    updateZoneType: builder.mutation<
      ZoneType,
      { zoneTypeId: string; data: UpdateZoneType }
    >({
      query: ({ zoneTypeId, data }) => ({
        url: `/zone-type/${zoneTypeId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['ZoneType', 'ZoneTypes'],
    }),
    deleteZoneType: builder.mutation<void, string>({
      query: (zoneTypeId) => ({
        url: `/zone-type/${zoneTypeId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ZoneType', 'ZoneTypes'],
    }),
  }),
});

export default zoneTypesService;
export const {
  useGetZoneTypesQuery,
  useGetZoneTypeByIdQuery,
  useCreateZoneTypeMutation,
  useUpdateZoneTypeMutation,
  useDeleteZoneTypeMutation,
} = zoneTypesService;
